import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { fetchPublications } from '../../fetchProjects';

const PublicationDetail = () => {
  const { idSlug } = useParams();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);

  // Extract numeric id from slug like "123-detection-spectral-analysis"
  const id = idSlug ? idSlug.split('-')[0] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPublications().then(data => {
      const found = data.find(p => String(p.id) === String(id));
      setPub(found || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-text-primary">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-accent" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!pub) {
    return (
      <div className="min-h-screen bg-background text-text-primary">
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>Publication Not Found | Sudhan R</title>
        </Helmet>
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Icon name="AlertCircle" size={48} className="text-red-400" />
          <h2 className="text-2xl font-bold">Publication Not Found</h2>
          <button onClick={() => navigate(-1)} className="text-accent hover:underline">Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  const keywords = (() => { try { return JSON.parse(pub.keywords || '[]'); } catch { return []; } })();

  return (
    <div className="min-h-screen bg-background text-text-primary selection:bg-accent/30 selection:text-white">
      <Helmet>
        <title>{pub.title} | Sudhan R Research</title>
        <meta name="description" content={pub.abstract || `${pub.title} by ${pub.authors}`} />
        <meta property="og:title" content={pub.title} />
        <meta property="og:description" content={pub.abstract || `${pub.title} by ${pub.authors}`} />
        {pub.image && <meta property="og:image" content={pub.image} />}
        <link rel="canonical" href={`https://sudhan.live/publication/${idSlug}`} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ScholarlyArticle",
                "@id": `https://sudhan.live/publication/${idSlug}#publication`,
                "headline": pub.title,
                "description": pub.abstract || "",
                "author": {
                  "@id": "https://sudhan.live/#person"
                },
                "publisher": {
                  "@id": "https://sudhan.live/#person"
                },
                "datePublished": pub.publishedDate || pub.year || "",
                "sameAs": pub.doi ? `https://doi.org/${pub.doi}` : "",
                "isPartOf": {
                  "@id": "https://sudhan.live/#website"
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://sudhan.live/publication/${idSlug}`
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": `https://sudhan.live/publication/${idSlug}#breadcrumb`,
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Resume",
                    "item": "https://sudhan.live/resume"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": pub.title,
                    "item": `https://sudhan.live/publication/${idSlug}`
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-secondary hover:text-accent font-medium mb-12 transition-smooth group"
        >
          <Icon name="ArrowLeft" size={20} className="group-hover:-translate-x-1 transition-transform" />
          Back to Full Resume
        </button>

        <article className="bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          {/* ── HEADER ── */}
          <header className="mb-10 relative z-10">

            {/* Row 1: Logo small + journal/year/DOI meta */}
            <div className="flex items-center gap-4 mb-6">
              {pub.image && (
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center p-1.5 overflow-hidden shadow-md border border-white/20 flex-shrink-0">
                  <Image src={pub.image} alt={pub.title} className="w-full h-full object-contain" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-text-secondary text-sm">
                {pub.journal && <span className="italic font-medium text-text-primary">{pub.journal}</span>}
                {pub.year && <><span className="text-white/20">·</span><span>{pub.publishedDate || pub.year}</span></>}
                {pub.publisher && <><span className="text-white/20">·</span><span>{pub.publisher}</span></>}
                {pub.doi && (
                  <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-accent hover:text-accent-light font-semibold transition-smooth">
                    <Icon name="Link2" size={13} /> DOI
                  </a>
                )}
              </div>
            </div>

            {/* Row 2: Full-width title */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-white/70 mb-4 leading-tight">
              {pub.title}
            </h1>

            {/* Row 3: Authors */}
            {pub.authors && (
              <p className="flex items-center gap-2 text-accent-light text-base font-semibold mb-5">
                <Icon name="Users" size={18} />
                {pub.authors}
              </p>
            )}

            {/* Row 4: Compact badges */}
            <div className="flex flex-wrap items-center gap-2">
              {pub.publicationType && (
                <span className="bg-primary-900/50 text-primary-300 border border-primary-700/50 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase">
                  {pub.publicationType}
                </span>
              )}
              {pub.status && (
                <span className="bg-white/5 border border-white/10 px-3 py-1 rounded-full text-xs font-medium text-text-secondary">
                  {pub.status}
                </span>
              )}
              {pub.featured === 1 && (
                <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <Icon name="Star" size={11} className="fill-current" /> Featured
                </span>
              )}
              {pub.award && (
                <span className="bg-rose-500/10 text-rose-400 border border-rose-500/20 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <Icon name="Trophy" size={11} /> {pub.award}
                </span>
              )}
              {pub.citations > 0 && (
                <span className="bg-white/5 border border-white/10 px-3 py-1 rounded-full text-xs text-text-muted flex items-center gap-1">
                  <Icon name="Quote" size={11} /> {pub.citations} citation{pub.citations !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </header>

          {/* ── ACTION BUTTONS ── */}
          {(pub.url || pub.pdfUrl || pub.codeUrl) && (
            <div className="flex flex-wrap gap-3 mb-10 pb-10 border-b border-white/10 relative z-10">
              {pub.url && (
                <a href={pub.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-accent-light bg-accent/10 border border-accent/20 hover:bg-accent/20 px-5 py-2.5 rounded-full transition-smooth font-bold">
                  <Icon name="ExternalLink" size={16} /> View Paper
                </a>
              )}
              {pub.pdfUrl && (
                <a href={pub.pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 px-5 py-2.5 rounded-full transition-smooth font-bold">
                  <Icon name="FileText" size={16} /> Download PDF
                </a>
              )}
              {pub.codeUrl && (
                <a href={pub.codeUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-text-secondary bg-white/5 border border-white/10 hover:bg-white/10 px-5 py-2.5 rounded-full transition-smooth font-medium">
                  <Icon name="Github" size={16} /> Code
                </a>
              )}
            </div>
          )}

          {/* ── ABSTRACT ── */}
          {pub.abstract && (
            <section className="mb-10 relative z-10">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                <Icon name="AlignLeft" size={18} className="text-accent" /> Abstract
              </h2>
              <p className="text-text-secondary leading-relaxed text-base">{pub.abstract}</p>
            </section>
          )}

          {/* ── DETAILS GRID ── */}
          {[pub.institution, pub.publisher, pub.fundedBy].some(Boolean) && (
            <section className="mb-10 relative z-10">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Publication Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: 'Building2', label: 'Institution', value: pub.institution },
                  { icon: 'Landmark', label: 'Publisher', value: pub.publisher },
                  { icon: 'Banknote', label: 'Funded By', value: pub.fundedBy },
                  { icon: 'Link2', label: 'DOI', value: pub.doi, href: pub.doi ? `https://doi.org/${pub.doi}` : null },
                ].filter(item => item.value).map(({ icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4">
                    <Icon name={icon} size={18} className="text-accent mt-0.5 shrink-0" />
                    <div>
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-0.5">{label}</p>
                      {href
                        ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-sm break-all">{value}</a>
                        : <p className="text-text-primary text-sm font-medium">{value}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── KEYWORDS ── */}
          {keywords.length > 0 && (
            <section className="relative z-10">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4">Keywords</h2>
              <div className="flex flex-wrap gap-2">
                {keywords.map(kw => (
                  <span key={kw} className="bg-accent/5 border border-accent/15 text-accent/80 px-3 py-1.5 rounded-full text-sm">
                    {kw}
                  </span>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PublicationDetail;
